import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import React, { useMemo, useState } from 'react'
import { useVariantSlice } from 'src/lib/listingVariants'
import type { SecurityCardKey } from 'src/lib/listingVariants/types'
import ReceiptLong from '../../../public/icons/svg/Security.svg'
import Shield from '../../../public/icons/svg/exclamation-triangle.svg'
import SupportAgent from '../../../public/icons/svg/hands-helping.svg'
import Policy from '../../../public/icons/svg/pencil-ruler.svg'
import { RedirectUrls } from '../../types/types'
import Carousel from '../Carousel/Carousel'

const ReportLocationModal = dynamic(() => import('./ReportLocationModal'))

type IProps = {
  locationId: string
}

type CardCopy = {
  title: string
  description: string
  icon: string
}

const Card = ({ card, index }: { card: CardCopy; index: number }) => {
  return (
    <article className="h-full w-[78vw] pb-2 md:w-full">
      <div className="flex h-full flex-col gap-4 border border-ink-100/20 bg-white-500 p-6">
        <div className="flex items-center justify-between">
          <span className="font-display text-[12px] tracking-[0.16em] text-ink-200 italic md:text-[13px]">
            {`0${index + 1}`}
          </span>
          <div className="flex size-9 items-center justify-center rounded-full border border-ink-100/30">
            <Image
              src={card.icon}
              alt={card.title}
              height={18}
              width={18}
              loading="lazy"
              style={{ width: 18, height: 18 }}
            />
          </div>
        </div>
        <h3 className="font-display text-[18px] leading-tight font-medium text-ink-700 md:text-[20px]">
          {card.title}
        </h3>
        <p className="text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {card.description}
        </p>
      </div>
    </article>
  )
}

function SecurityAndSupport({ locationId }: IProps) {
  const { t } = useTranslation('details')
  const sections = useVariantSlice('sections')
  const { headlineKey, accentKey, subtitleKey, cardKeys } =
    sections.securityAndSupport

  const [modalReportOpen, setReportModalOpen] = useState<boolean>(false)

  // Resolve the 4 cards declared by the variant. Each `SecurityCardKey`
  // maps to (icon, title, description) — built with literal `t()` calls
  // so the typed-key contract is preserved.
  const cards = useMemo<CardCopy[]>(() => {
    const cardMap: Record<SecurityCardKey, CardCopy> = {
      verification_monthly: {
        icon: ReceiptLong,
        title: t('SECURITY.CARDS.VERIFICATION_MONTHLY.TITLE'),
        description: t('SECURITY.CARDS.VERIFICATION_MONTHLY.DESCRIPTION')
      },
      verification_b2b: {
        icon: ReceiptLong,
        title: t('SECURITY.CARDS.VERIFICATION_B2B.TITLE'),
        description: t('SECURITY.CARDS.VERIFICATION_B2B.DESCRIPTION')
      },
      verification_traditional: {
        icon: ReceiptLong,
        title: t('SECURITY.CARDS.VERIFICATION_TRADITIONAL.TITLE'),
        description: t('SECURITY.CARDS.VERIFICATION_TRADITIONAL.DESCRIPTION')
      },
      anti_fraud: {
        icon: Shield,
        title: t('SECURITY.CARDS.ANTI_FRAUD.TITLE'),
        description: t('SECURITY.CARDS.ANTI_FRAUD.DESCRIPTION')
      },
      control_monthly: {
        icon: Policy,
        title: t('SECURITY.CARDS.CONTROL_MONTHLY.TITLE'),
        description: t('SECURITY.CARDS.CONTROL_MONTHLY.DESCRIPTION')
      },
      custom_terms_b2b: {
        icon: Policy,
        title: t('SECURITY.CARDS.CUSTOM_TERMS_B2B.TITLE'),
        description: t('SECURITY.CARDS.CUSTOM_TERMS_B2B.DESCRIPTION')
      },
      host_direct_traditional: {
        icon: Policy,
        title: t('SECURITY.CARDS.HOST_DIRECT_TRADITIONAL.TITLE'),
        description: t('SECURITY.CARDS.HOST_DIRECT_TRADITIONAL.DESCRIPTION')
      },
      support_monthly: {
        icon: SupportAgent,
        title: t('SECURITY.CARDS.SUPPORT_MONTHLY.TITLE'),
        description: t('SECURITY.CARDS.SUPPORT_MONTHLY.DESCRIPTION')
      },
      support_specialist_b2b: {
        icon: SupportAgent,
        title: t('SECURITY.CARDS.SUPPORT_SPECIALIST_B2B.TITLE'),
        description: t('SECURITY.CARDS.SUPPORT_SPECIALIST_B2B.DESCRIPTION')
      },
      support_specialist_traditional: {
        icon: SupportAgent,
        title: t('SECURITY.CARDS.SUPPORT_SPECIALIST_TRADITIONAL.TITLE'),
        description: t(
          'SECURITY.CARDS.SUPPORT_SPECIALIST_TRADITIONAL.DESCRIPTION'
        )
      }
    }
    return cardKeys.map((key) => cardMap[key])
  }, [cardKeys, t])

  // The variant picks WHICH headline/accent/subtitle to use; the
  // component does the lookup via a literal-keyed map so the typed-`t()`
  // contract is preserved. Same pattern as the card map above.
  const HEADLINE_MAP: Record<string, string> = {
    'SECURITY.MONTHLY.TITLE_PREFIX': t('SECURITY.MONTHLY.TITLE_PREFIX'),
    'SECURITY.BROKER.TITLE_PREFIX': t('SECURITY.BROKER.TITLE_PREFIX'),
    'SECURITY.TRADITIONAL.TITLE_PREFIX': t('SECURITY.TRADITIONAL.TITLE_PREFIX')
  }
  const ACCENT_MAP: Record<string, string> = {
    'SECURITY.MONTHLY.TITLE_ACCENT': t('SECURITY.MONTHLY.TITLE_ACCENT'),
    'SECURITY.BROKER.TITLE_ACCENT': t('SECURITY.BROKER.TITLE_ACCENT'),
    'SECURITY.TRADITIONAL.TITLE_ACCENT': t('SECURITY.TRADITIONAL.TITLE_ACCENT')
  }
  const SUBTITLE_MAP: Record<string, string> = {
    'SECURITY.MONTHLY.SUBTITLE': t('SECURITY.MONTHLY.SUBTITLE'),
    'SECURITY.BROKER.SUBTITLE': t('SECURITY.BROKER.SUBTITLE'),
    'SECURITY.TRADITIONAL.SUBTITLE': t('SECURITY.TRADITIONAL.SUBTITLE')
  }
  const titlePrefix = HEADLINE_MAP[headlineKey] ?? ''
  const titleAccent = ACCENT_MAP[accentKey] ?? ''
  const subtitle = SUBTITLE_MAP[subtitleKey] ?? ''

  return (
    <section className="border-t border-ink-100/15 py-12 md:py-24">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('SECURITY.EYEBROW')}
          </p>
          <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
            {`${titlePrefix} `}
            <span className="font-display text-blue-500 italic">
              {titleAccent}
            </span>
          </h2>
        </div>
        <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
          {subtitle}
        </p>
      </div>

      {/* Mobile carousel */}
      <div className="mt-10 md:hidden">
        <Carousel
          items={cards}
          carouselOptions={{
            align: 'center',
            slidesToScroll: 1,
            loop: true
          }}
          showDotsMb
        >
          {({ item, index }) => (
            <Card card={item as CardCopy} index={index ?? 0} />
          )}
        </Carousel>
      </div>

      {/* Desktop grid */}
      <div className="mt-14 hidden gap-5 md:grid md:grid-cols-2 lg:grid-cols-4">
        {cards.map((item, key) => (
          <Card key={key} card={item} index={key} />
        ))}
      </div>

      <div className="mt-10 flex flex-col gap-3 border-t border-ink-100/15 pt-8 md:mt-14 md:flex-row md:items-center md:justify-between md:gap-6">
        <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {t('SECURITY.BOTTOM.PROMPT')}
        </p>
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          <Link
            href={RedirectUrls.Help}
            className="inline-flex items-center justify-center border border-ink-700 bg-ink-700 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:border-blue-500 hover:bg-blue-500"
          >
            {t('SECURITY.BOTTOM.HELP_CTA')}
          </Link>
          <button
            type="button"
            onClick={() => setReportModalOpen(true)}
            className="inline-flex items-center justify-center border border-ink-100/40 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500"
          >
            {t('SECURITY.BOTTOM.REPORT_CTA')}
          </button>
        </div>
      </div>

      <ReportLocationModal
        isOpen={modalReportOpen}
        setIsOpen={setReportModalOpen}
        locationId={locationId}
      />
    </section>
  )
}

export default React.memo(SecurityAndSupport)
